import type { CSSProperties, FC } from "react";
import { token } from "@stubhub/broadway-foundation/tokens";
import { PerformerMerchLabel } from "src/website-apps/shared/components/hero-banner/PerformerMerchLabel";
import {
  isPerformerCredibilityBadge,
  PerformerLabelType,
  useWillShowPerformerCredibilityLabel,
} from "src/website-apps/shared/hooks/useWillShowPerformerCredibilityLabel";
import { MobileCredibilityBadgePlacement, useMobileCredibilityBadgePlacement } from "./MobileCredibilityBadgePlacement";

const MOBILE_VARIANT_TEXT_STYLE = {
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "16px",
} satisfies Pick<CSSProperties, "fontSize" | "fontStyle" | "fontWeight" | "lineHeight">;

const MOBILE_HERO_MLB_ICON_DIMENSIONS = { width: 29, height: 16 } as const;
const MOBILE_BELOW_HERO_MLB_ICON_DIMENSIONS = { width: 32, height: 18 } as const;

interface Props {
  placement: MobileCredibilityBadgePlacement;
  className?: string;
  wrapperClassName?: string;
  visible?: boolean;
}

export const MobileCredibilityBadge: FC<Props> = ({ placement, className, wrapperClassName, visible = true }) => {
  const assignedPlacement = useMobileCredibilityBadgePlacement();
  const labelType = useWillShowPerformerCredibilityLabel();
  const isCredibilityBadge = isPerformerCredibilityBadge(labelType);
  const isAssignedPlacement = isCredibilityBadge
    ? assignedPlacement === placement
    : placement === MobileCredibilityBadgePlacement.CONTROL;

  if (!visible || labelType === PerformerLabelType.NONE || !isAssignedPlacement) return null;

  const isControl = placement === MobileCredibilityBadgePlacement.CONTROL;
  const variantColor =
    labelType === PerformerLabelType.MLB
      ? token(placement === MobileCredibilityBadgePlacement.HERO ? "colors.text" : "colors.text.info")
      : token("colors.text.brand");
  const variantBackground =
    labelType === PerformerLabelType.MLB
      ? token(placement === MobileCredibilityBadgePlacement.HERO ? "colors.bg.surface" : "colors.bg.surface.info")
      : undefined;
  const partnerIconDimensions =
    placement === MobileCredibilityBadgePlacement.HERO
      ? MOBILE_HERO_MLB_ICON_DIMENSIONS
      : MOBILE_BELOW_HERO_MLB_ICON_DIMENSIONS;
  const badge = (
    <PerformerMerchLabel
      className={className}
      {...(isControl
        ? {}
        : {
            compactMlbLabel: true,
            color: variantColor,
            partnerIconDimensions,
            textStyle: MOBILE_VARIANT_TEXT_STYLE,
            ...(variantBackground !== undefined && { background: variantBackground }),
            ...(labelType === PerformerLabelType.OFFICIAL_PARTNER && { iconColor: variantColor }),
          })}
    />
  );

  return wrapperClassName ? <div className={wrapperClassName}>{badge}</div> : badge;
};
